/**
 * Firebase Index Helper
 * Provides helpful information about required Firestore indexes
 */

export const REQUIRED_INDEXES = {
  c3_tasks_workload: {
    collection: 'c3_tasks',
    fields: ['incidentId', 'orgId', 'status'],
    description: 'Required for smart task workload analysis',
    createUrl: 'https://console.firebase.google.com/v1/r/project/crisiscompass-prod/firestore/indexes?create_composite=ClNwcm9qZWN0cy9jcmlzaXNjb21wYXNzLXByb2QvZGF0YWJhc2VzLyhkZWZhdWx0KS9jb2xsZWN0aW9uR3JvdXBzL2MzX3Rhc2tzL2luZGV4ZXMvXxABGg4KCmluY2lkZW50SWQQARoJCgVvcmdJZBABGgoKBnN0YXR1cxABGgwKCF9fbmFtZV9fEAE'
  },
  c3_updates_communication: {
    collection: 'c3_updates',
    fields: ['incidentId', 'orgId', 'timestamp'],
    description: 'Required for communication gap analysis',
    createUrl: 'https://console.firebase.google.com/v1/r/project/crisiscompass-prod/firestore/indexes?create_composite=ClVwcm9qZWN0cy9jcmlzaXNjb21wYXNzLXByb2QvZGF0YWJhc2VzLyhkZWZhdWx0KS9jb2xsZWN0aW9uR3JvdXBzL2MzX3VwZGF0ZXMvaW5kZXhlcy9fEAEaDgoKaW5jaWRlbnRJZBABGgkKBW9yZ0lkEAEaDQoJdGltZXN0YW1wEAEaDAoIX19uYW1lX18QAQ'
  }
};

/**
 * Check if an error is related to missing Firestore indexes
 */
export const isIndexError = (error) => {
  if (!error || !error.message) return false;
  
  const message = error.message.toLowerCase();
  return message.includes('index') || 
         message.includes('composite') ||
         message.includes('requires an index');
};

/**
 * Check if an error is related to insufficient permissions
 */
export const isPermissionError = (error) => {
  if (!error) return false;
  
  const message = error.message?.toLowerCase() || '';
  const code = error.code?.toLowerCase() || '';
  
  return message.includes('permission') || 
         message.includes('denied') ||
         message.includes('insufficient') ||
         code.includes('permission-denied') ||
         code.includes('unauthenticated');
};

/**
 * Check if an error is related to write operations
 */
export const isWriteError = (error) => {
  if (!error) return false;
  
  const message = error.message?.toLowerCase() || '';
  
  return message.includes('write') ||
         message.includes('add') ||
         message.includes('create') ||
         message.includes('update') ||
         message.includes('400 (bad request)');
};

/**
 * Get helpful error message for index-related errors
 */
export const getIndexErrorMessage = (error) => {
  if (!isIndexError(error)) return null;
  
  // Extract collection name from error message if possible
  const message = error.message;
  let collectionName = 'unknown';
  
  if (message.includes('c3_tasks')) {
    collectionName = 'c3_tasks';
  } else if (message.includes('c3_updates')) {
    collectionName = 'c3_updates';
  }
  
  return {
    type: 'index_required',
    collection: collectionName,
    message: `Firestore composite index required for ${collectionName} collection`,
    suggestion: 'The system will continue with limited functionality. Contact your administrator to create the required indexes.',
    indexInfo: Object.values(REQUIRED_INDEXES).find(idx => idx.collection === collectionName)
  };
};

/**
 * Get helpful error message for permission-related errors
 */
export const getPermissionErrorMessage = (error) => {
  if (!isPermissionError(error)) return null;
  
  return {
    type: 'permission_denied',
    message: 'Insufficient permissions for this operation',
    suggestion: 'Some automation features may be limited. Contact your administrator if you need access to these features.'
  };
};

/**
 * Log helpful error information to console
 */
export const logHelpfulError = (error, context = 'Firebase Operation') => {
  if (isPermissionError(error)) {
    console.group(`🔒 Permission Error (${context})`);
    console.warn('⚠️ Insufficient permissions for this operation');
    console.info('ℹ️ Some automation features may be limited. Contact your administrator if you need access to these features.');
    console.groupEnd();
    return;
  }
  
  if (isIndexError(error)) {
    console.group(`🔍 Index Required (${context})`);
    console.warn('⚠️ Firestore composite index required for this query');
    console.info('ℹ️ The system will continue with limited functionality. Contact your administrator to create the required indexes.');
    
    // Try to extract collection name from error
    const message = error.message || '';
    if (message.includes('c3_tasks')) {
      console.info('📋 Missing index for c3_tasks collection');
      console.info('🔗 Create index: https://console.firebase.google.com/v1/r/project/crisiscompass-prod/firestore/indexes?create_composite=ClNwcm9qZWN0cy9jcmlzaXNjb21wYXNzLXByb2QvZGF0YWJhc2VzLyhkZWZhdWx0KS9jb2xsZWN0aW9uR3JvdXBzL2MzX3Rhc2tzL2luZGV4ZXMvXxABGg4KCmluY2lkZW50SWQQARoJCgVvcmdJZBABGgoKBnN0YXR1cxABGgwKCF9fbmFtZV9fEAE');
    } else if (message.includes('c3_updates')) {
      console.info('📢 Missing index for c3_updates collection');
      console.info('🔗 Create index: https://console.firebase.google.com/v1/r/project/crisiscompass-prod/firestore/indexes?create_composite=ClVwcm9qZWN0cy9jcmlzaXNjb21wYXNzLXByb2QvZGF0YWJhc2VzLyhkZWZhdWx0KS9jb2xsZWN0aW9uR3JvdXBzL2MzX3VwZGF0ZXMvaW5kZXhlcy9fEAEaDgoKaW5jaWRlbnRJZBABGgkKBW9yZ0lkEAEaDQoJdGltZXN0YW1wEAEaDAoIX19uYW1lX18QAQ');
    }
    
    console.groupEnd();
    return;
  }
  
  if (isWriteError(error)) {
    console.group(`✍️ Write Error (${context})`);
    console.warn('⚠️ Unable to complete write operation');
    console.info('ℹ️ This may be due to permission restrictions or network issues.');
    console.info('ℹ️ Core functionality continues to work normally.');
    console.groupEnd();
    return;
  }
  
  // For other errors, log with context but don't suppress
  console.group(`❌ Error (${context})`);
  console.error('Unexpected error occurred:', error);
  console.groupEnd();
};

/**
 * Create a graceful error handler for automation services
 */
export const createGracefulErrorHandler = (serviceName) => {
  return (error, operationName = '') => {
    const context = `${serviceName}${operationName ? ` - ${operationName}` : ''}`;
    logHelpfulError(error, context);
    
    // Return appropriate fallback based on error type
    if (isIndexError(error) || isPermissionError(error)) {
      return null; // Graceful degradation
    }
    
    throw error; // Re-throw unexpected errors
  };
}; 